import { useCallback } from 'react';

import {
  changeExportConfigStatusApi,
  createExportConfigApi,
  getExportConfigApi,
  removeExportConfigApi,
  updateExportConfigApi,
} from '../../../api/exportApi';
import { Actions } from '../../../constants/actionTypes';
import { toastErrorTranslated } from '../../../helpers/toast';
import type { TExportConfig } from '../../../interfaces/redux/TSettingsState';
import { useMainDispatch } from '../../useMainDispatch';

export const useExportConfigActions = () => {
  const dispatch = useMainDispatch();

  const deleteExportConfig = useCallback(
    async (id: string) => {
      try {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: true } });
        await removeExportConfigApi(id);
        dispatch({
          type: Actions.REMOVE_EXPORT_CONFIG,
          payload: id,
        });
      } catch (error) {
        toastErrorTranslated(error);
      } finally {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: false } });
      }
    },
    [dispatch],
  );

  const setExportConfigStatus = useCallback(
    async (exportId: string, status: boolean) => {
      try {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: true } });
        await changeExportConfigStatusApi(exportId, status);
        dispatch({
          type: Actions.SET_EXPORT_CONFIG_STATUS,
          payload: {
            id: exportId,
            status,
          },
        });
      } catch (error) {
        toastErrorTranslated(error);
      } finally {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: false } });
      }
    },
    [dispatch],
  );

  const setSelectedExportConfig = useCallback(
    (exportConfig: TExportConfig) => {
      dispatch({
        type: Actions.SET_SELECTED_EXPORT_CONFIG,
        payload: exportConfig,
      });
    },
    [dispatch],
  );

  const createExportConfig = useCallback(
    async (exportConfig: TExportConfig) => {
      try {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: true } });
        await createExportConfigApi(exportConfig);
        dispatch({ type: Actions.ADD_EXPORT_CONFIG, payload: exportConfig });
      } catch (error) {
        toastErrorTranslated(error);
      } finally {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: false } });
      }
    },
    [dispatch],
  );

  const editExportConfig = useCallback(
    async (exportConfig: TExportConfig) => {
      try {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: true } });
        await updateExportConfigApi(exportConfig);
        dispatch({ type: Actions.EDIT_EXPORT_CONFIG, payload: exportConfig });
      } catch (error) {
        toastErrorTranslated(error);
      } finally {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: false } });
      }
    },
    [dispatch],
  );

  const getExportConfigs = useCallback(async () => {
    try {
      dispatch({ type: Actions.SET_UI, payload: { isLoading: true } });
      const { data: exportConfigs } = await getExportConfigApi();
      dispatch({
        type: Actions.SET_SETTINGS,
        payload: { exportConfigs: exportConfigs },
      });
    } catch (error) {
      toastErrorTranslated(error);
    } finally {
      dispatch({ type: Actions.SET_UI, payload: { isLoading: false } });
    }
  }, [dispatch]);

  return {
    deleteExportConfig,
    getExportConfigs,
    setExportConfigStatus,
    setSelectedExportConfig,
    createExportConfig,
    editExportConfig,
  };
};
